import React, { forwardRef, useMemo, useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Chip,
  Checkbox,
  Dialog,
  Divider,
  IconButton,
  Slide,
  Toolbar,
  Typography,
  Stack,
  Button,
  Paper,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import request from "../utilities/requests";
import { toast } from "../utilities/SnackBarUtils";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function formatCents(amountCents, currency = "usd") {
  const v = Number(amountCents || 0) / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return `$${v.toFixed(2)}`;
  }
}

export default function OrdersDialog({ open, onClose, data }) {
  const [orders, setOrders] = useState([]);
  const [eventName, setEventName] = useState("");
  const [eventStart, setEventStart] = useState("");
  const [selectionContext, setSelectionContext] = useState(null); // { orderId, eventId }
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [assignEditing, setAssignEditing] = useState({}); // { [itemId]: true }
  const [assignForm, setAssignForm] = useState({}); // { [itemId]: { firstName, lastName, email, loading, errors } }

  useEffect(() => {
    if (data) {
      setOrders(data?.orders || []);
      setEventName(data?.event?.name || "");
      setEventStart(data?.event?.start || "");
    } else if (!data && open) {
      getOrders();
    }
  }, [data, open]);

  const getOrders = async () => {
    const result = await request("ticketing/orders");
    if (result.success && result.data && Array.isArray(result.data.orders)) {
      setOrders(result.data.orders);
    } else {
      setOrders([]);
    }
  };

  const header = useMemo(() => {
    if (!eventName) return "Order Details";
    return `Order Details – ${eventName}`;
  }, [eventName]);

  const currency = orders?.[0]?.currency || "usd";

  const totalRefundableCents = useMemo(
    () => orders.reduce((sum, o) => sum + (o.refundableAmountCents || 0), 0),
    [orders]
  );

  const hasAnyRefundable = useMemo(
    () => orders.some((o) => (o.items || []).some((it) => it.refundable)),
    [orders]
  );

  const toggleSelect = (itemId, refundable, eventId, orderId) => {
    if (!refundable) return; // ignore clicks on non-refundable

    setSelectedIds((prev) => {
      // Enforce single-order selection
      if (selectionContext && selectionContext.orderId !== orderId) {
        toast.error("You can only select tickets from one order at a time.");
        return new Set(prev); // no change
      }

      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);

      // Update/clear selection context
      if (next.size === 0) {
        setSelectionContext(null);
      } else if (!selectionContext) {
        setSelectionContext({ orderId, eventId });
      }

      return next;
    });
  };

  const startInlineAssign = (itemId) => {
    setAssignEditing((s) => ({ ...s, [itemId]: true }));
    setAssignForm((s) => ({
      ...s,
      [itemId]: s[itemId] || {
        firstName: "",
        lastName: "",
        email: "",
        loading: false,
        errors: {},
      },
    }));
  };

  const changeInlineAssign = (itemId, field, value) => {
    setAssignForm((s) => ({
      ...s,
      [itemId]: {
        ...(s[itemId] || {}),
        [field]: value,
        errors: { ...(s[itemId]?.errors || {}), [field]: undefined },
      },
    }));
  };

  const validateInlineAssign = (vals) => {
    const errs = {};
    if (!vals.firstName?.trim()) errs.firstName = "Required";
    if (!vals.lastName?.trim()) errs.lastName = "Required";
    if (!vals.email?.trim()) errs.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vals.email))
      errs.email = "Invalid email";
    return errs;
  };

  const assignToMe = async (orderId, itemId) => {
    const body = { orderId, itemId, self: true };
    const res = await request("ticketing/assign", "post", body);
    if (!res?.success) {
      toast.error(res?.error || "Failed to assign ticket");
      return;
    } else {
      toast.success("Ticket assigned to you");
      handleClose();
    }
  };

  const submitInlineAssign = async (orderId, itemId) => {
    const vals = assignForm[itemId] || {};
    const errs = validateInlineAssign(vals);
    if (Object.keys(errs).length) {
      setAssignForm((s) => ({
        ...s,
        [itemId]: { ...(s[itemId] || {}), errors: errs },
      }));
      return;
    }
    try {
      setAssignForm((s) => ({
        ...s,
        [itemId]: { ...(s[itemId] || {}), loading: true },
      }));
      const body = {
        orderId,
        itemId,
        assignee: {
          firstName: vals.firstName.trim(),
          lastName: vals.lastName.trim(),
          email: vals.email.trim(),
        },
      };
      const res = await request("ticketing/assign", "post", body);
      if (!res?.success) {
        toast.error(res?.error || "Failed to assign ticket");
        return;
      }
      toast.success(`Ticket assigned to ${vals.firstName}`);
      // Close the inline editor
      setAssignEditing((s) => ({ ...s, [itemId]: false }));
      handleClose();
    } catch (e) {
      toast.error(e.message || "Failed to assign ticket");
    } finally {
      setAssignForm((s) => ({
        ...s,
        [itemId]: { ...(s[itemId] || {}), loading: false },
      }));
    }
  };

  const selectedTotalCents = useMemo(() => {
    let sum = 0;
    for (const o of orders) {
      for (const it of o.items || []) {
        if (it.refundable && selectedIds.has(it.itemId)) {
          sum += Number(it.amountCents || 0);
        }
      }
    }
    return sum;
  }, [orders, selectedIds]);

  const selectedItems = useMemo(() => {
    const out = [];
    for (const o of orders) {
      for (const it of o.items || []) {
        if (it.refundable && selectedIds.has(it.itemId)) {
          out.push({ orderId: o.orderId, itemId: it.itemId });
        }
      }
    }
    return out;
  }, [orders, selectedIds]);

  const handleProcessRefund = async () => {
    if (selectedItems.length === 0 || submitting) return;
    // Determine the single eventId for the selected order
    let eventIdForRefund = selectionContext?.eventId;
    if (!eventIdForRefund && selectedItems.length > 0) {
      const firstOrderId = selectedItems[0].orderId;
      const foundOrder = orders.find((o) => o.orderId === firstOrderId);
      eventIdForRefund = foundOrder?.event?.id;
    }

    if (!eventIdForRefund) {
      toast.error("Could not determine event for refund.");
      return;
    }

    const body = {
      eventId: eventIdForRefund,
      selections: selectedItems, // [{ orderId, itemId }]
      reason: "requested_by_customer",
    };

    try {
      setSubmitError("");
      setSubmitting(true);
      const result = await request("ticketing/refund", "post", body);
      if (!result || result.success !== true) {
        toast.error(result?.error || "Refund request failed");
        return;
      }

      // Show success UI instead of closing immediately
      setSuccess(true);
      // Optionally clear selection
      setSelectedIds(new Set());
    } catch (e) {
      setSubmitError(e.message || "Refund request failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setAssignEditing({});
    setSelectionContext(null);
    setSelectedIds(new Set());
    setSelectionContext(null);
    onClose();
  };

  return (
    <Dialog
      fullScreen
      open={Boolean(open)}
      onClose={handleClose}
      TransitionComponent={Transition}
      keepMounted
    >
      <AppBar sx={{ position: "relative" }} color="default" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{ ml: 2, flex: 1, fontWeight: 700 }}
            variant="body1"
            component="div"
          >
            {header}
          </Typography>
        </Toolbar>
        {eventStart && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Event starts: {new Date(eventStart).toLocaleString()}
            </Typography>
          </Box>
        )}
      </AppBar>

      <Box sx={{ p: 2, pb: 12 }}>
        {success ? (
          <Box
            sx={{
              maxWidth: 560,
              mx: "auto",
              textAlign: "center",
              py: 8,
              px: 2,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 56, color: "success.main" }}
            />
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
              Refund request submitted
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your refund request has been received. You’ll receive a
              confirmation email shortly.
            </Typography>
          </Box>
        ) : (
          <>
            {orders.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="h6" gutterBottom>
                  No refundable tickets found
                </Typography>
                <Typography variant="body2">
                  If you purchased your ticket on a different ticketing
                  platform, please view the order there
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  If you believe this is a mistake, please contact support.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  component={Link}
                  to="/contactus"
                  sx={{ mt: 2 }}
                >
                  Contact Support
                </Button>
              </Box>
            ) : (
              <>
                <Stack spacing={3} sx={{ maxWidth: 720, mx: "auto" }}>
                  {orders.map((o) => (
                    <Box
                      key={o.orderId}
                      sx={{
                        borderColor: "divider",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Stack
                          direction="column"
                          spacing={0.5}
                          alignItems="flex-start"
                          sx={{ minWidth: 0 }}
                        >
                          <Typography
                            variant="subtitle2"
                            title={`Order #${o.orderId}`}
                            sx={{
                              fontWeight: 700,
                              minWidth: 0,
                              maxWidth: { xs: "100%", sm: 360 },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontFamily:
                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            }}
                          >
                            {`Order #${o.customerOrderId}`}
                          </Typography>
                          {o.paidAt && (
                            <Chip
                              size="small"
                              label={`Paid ${new Date(
                                o.paidAt
                              ).toLocaleDateString()}`}
                              sx={{ mt: { xs: 0.5, sm: 0 } }}
                            />
                          )}
                        </Stack>
                        <Stack spacing={1}>
                          <Typography
                            variant="subtitle1"
                            align="center"
                            sx={{ fontWeight: 700 }}
                          >
                            {formatCents(o.amountTotalCents, o.currency)}
                          </Typography>
                          {(o.refundableAmountCents && o.refundableAmountCents > 0) ? (
                            <Chip
                              label={`Eligible: ${formatCents(
                                o.refundableAmountCents,
                                currency
                              )}`}
                              color="success"
                              size="small"
                            />
                          ) : null}
                        </Stack>
                      </Box>
                      <Divider />
                      <Box sx={{ p: 2 }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr",
                            gap: 2,
                          }}
                        >
                          {o.items.map((it) => {
                            const selected =
                              it.refundable && selectedIds.has(it.itemId);
                            const disabled =
                              !it.refundable ||
                              (selectionContext &&
                                selectionContext.orderId !== o.orderId);
                            return (
                              <Box
                                key={it.itemId}
                                // onClick={() =>
                                //   toggleSelect(it.itemId, it.refundable)
                                // }
                                sx={{
                                  position: "relative",
                                  border: "1px solid",
                                  borderColor: selected
                                    ? "primary.main"
                                    : "divider",
                                  bgcolor: selected
                                    ? "action.selected"
                                    : "background.paper",
                                  borderRadius: 2,
                                  p: 2,
                                  cursor: disabled ? "default" : "pointer",
                                  opacity: disabled ? 0.6 : 1,
                                  transition:
                                    "border-color 120ms ease, background-color 120ms ease",
                                  "&:hover": {
                                    borderColor: disabled
                                      ? "divider"
                                      : "primary.main",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSelect(
                                      it.itemId,
                                      it.refundable,
                                      o.event.id,
                                      o.orderId
                                    );
                                  }}
                                >
                                  <Checkbox
                                    edge="start"
                                    tabIndex={-1}
                                    disableRipple
                                    checked={selected}
                                    disabled={!it.refundable}
                                    inputProps={{
                                      "aria-label": it.ticketTypeName,
                                    }}
                                  />
                                </Box>

                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 700, pr: 5 }}
                                >
                                  {it.ticketTypeName}
                                </Typography>

                                {it.claimStatus === "claimed" && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block" }}
                                  >
                                    Assigned to: {it.assigneeName || "Claimed"}
                                  </Typography>
                                )}
                                {(it.claimStatus === "unassigned" ||
                                  (it.claimStatus === "claimed" &&
                                    it.assignable === true)) &&
                                  (assignEditing[it.itemId] ? (
                                    <Box
                                      sx={{
                                        mt: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1.5,
                                        p: 1.5,
                                        bgcolor: "#f7f7f7",
                                        maxWidth: 300,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ fontWeight: 700 }}
                                        >
                                          {it.claimStatus === "claimed"
                                            ? "Reassign ticket"
                                            : "Assign ticket"}
                                        </Typography>
                                        <Tooltip
                                          arrow
                                          title="The assignee will receive an email that they have a ticket to the event. You can change the assignee at any time before the event starts"
                                        >
                                          <InfoOutlinedIcon
                                            fontSize="small"
                                            sx={{ color: "text.secondary" }}
                                          />
                                        </Tooltip>
                                        <Button
                                          size="small"
                                          onClick={() =>
                                            assignToMe(o.orderId, it.itemId)
                                          }
                                          sx={{ ml: 1 }}
                                        >
                                          Assign to me
                                        </Button>
                                      </Box>
                                      <Stack
                                        direction="column"
                                        spacing={1}
                                        alignItems={{
                                          xs: "stretch",
                                          sm: "flex-start",
                                        }}
                                      >
                                        <TextField
                                          label="First name"
                                          size="small"
                                          value={
                                            assignForm[it.itemId]?.firstName ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            changeInlineAssign(
                                              it.itemId,
                                              "firstName",
                                              e.target.value
                                            )
                                          }
                                          error={Boolean(
                                            assignForm[it.itemId]?.errors
                                              ?.firstName
                                          )}
                                          helperText={
                                            assignForm[it.itemId]?.errors
                                              ?.firstName || " "
                                          }
                                          fullWidth
                                        />
                                        <TextField
                                          label="Last name"
                                          size="small"
                                          value={
                                            assignForm[it.itemId]?.lastName ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            changeInlineAssign(
                                              it.itemId,
                                              "lastName",
                                              e.target.value
                                            )
                                          }
                                          error={Boolean(
                                            assignForm[it.itemId]?.errors
                                              ?.lastName
                                          )}
                                          helperText={
                                            assignForm[it.itemId]?.errors
                                              ?.lastName || " "
                                          }
                                          fullWidth
                                        />
                                        <TextField
                                          label="Email"
                                          size="small"
                                          type="email"
                                          value={
                                            assignForm[it.itemId]?.email || ""
                                          }
                                          onChange={(e) =>
                                            changeInlineAssign(
                                              it.itemId,
                                              "email",
                                              e.target.value
                                            )
                                          }
                                          error={Boolean(
                                            assignForm[it.itemId]?.errors?.email
                                          )}
                                          helperText={
                                            assignForm[it.itemId]?.errors
                                              ?.email || " "
                                          }
                                          fullWidth
                                        />
                                        <Button
                                          variant="contained"
                                          size="small"
                                          disabled={
                                            assignForm[it.itemId]?.loading
                                          }
                                          onClick={() =>
                                            submitInlineAssign(
                                              o.orderId,
                                              it.itemId
                                            )
                                          }
                                          fullWidth
                                        >
                                          {assignForm[it.itemId]?.loading
                                            ? it.claimStatus === "claimed"
                                              ? "Reassigning…"
                                              : "Assigning…"
                                            : it.claimStatus === "claimed"
                                            ? "Reassign"
                                            : "Assign"}
                                        </Button>
                                      </Stack>
                                    </Box>
                                  ) : (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      sx={{ mt: 1 }}
                                      onClick={() =>
                                        startInlineAssign(it.itemId)
                                      }
                                    >
                                      {it.claimStatus === "claimed"
                                        ? "Reassign"
                                        : "Assign Ticket"}
                                    </Button>
                                  ))}
                                <Box
                                  sx={{
                                    mt: 1,
                                    width: "100%",
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ display: "block", mt: 0.5 }}
                                      color={
                                        it.refundable
                                          ? "success.main"
                                          : "text.secondary"
                                      }
                                    >
                                      {it.refundable
                                        ? "Eligible for refund"
                                        : it.reason || "Not eligible"}
                                    </Typography>
                                    {it.refundable && (
                                      <Chip
                                        size="small"
                                        label={formatCents(
                                          it.amountCents,
                                          o.currency
                                        )}
                                        color={selected ? "primary" : "default"}
                                      />
                                    )}
                                  </Stack>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
                {!hasAnyRefundable && (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography variant="h6" gutterBottom>
                      No refundable tickets found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      If you believe this is a mistake, please contact support.
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      component={Link}
                      to="/contactus"
                      sx={{ mt: 2 }}
                    >
                      Contact Support
                    </Button>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>

      {submitError && (
        <Box sx={{ maxWidth: 720, mx: "auto", px: 2, pb: 1 }}>
          <Typography variant="body2" color="error.main">
            {submitError}
          </Typography>
        </Box>
      )}

      {/* Sticky bottom action bar */}
      <Paper
        square
        elevation={8}
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          py: 1.5,
          px: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            maxWidth: 720,
            mx: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {success ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Button variant="contained" color="primary" onClick={handleClose}>
                Close
              </Button>
            </Box>
          ) : (
            <>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {selectedTotalCents > 0
                    ? `Selected total: ${formatCents(
                        selectedTotalCents,
                        currency
                      )}`
                    : "Select ticket(s) to refund"}
                </Typography>
                {/* {invalidSelection && (
                  <Typography variant="caption" color="error.main">
                    You can’t refund only assigned tickets when unassigned
                    tickets remain in this order. Include the unassigned
                    ticket(s) too.
                  </Typography>
                )} */}
              </Box>
              <Button
                variant="contained"
                color="primary"
                disabled={selectedItems.length === 0 || submitting}
                onClick={handleProcessRefund}
              >
                {submitting ? "Processing…" : "Process refund"}
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Dialog>
  );
}
